import * as Backbone from "backbone";
import * as _ from "lodash";
import { AppCollection } from "../../../../collections/Apps";
import { DashboardCategoryCollection } from "../../../../collections/DashboardCategories";
import { DashboardChannelCollection } from "../../../../collections/DashboardChannels";
import { DashboardWidgetCollection } from "../../../../collections/DashboardWidgets";
import { DashboardTabCollection } from "../../../../collections/DashboardTabs";
import { DashboardCollection } from "../../../../collections/Dashboards";
import { InstallCollection } from "../../../../collections/Installs";
import { ModuleCollection } from "../../../../collections/Modules";
import { OrganizationCollection } from "../../../../collections/Organizations";
import { PermissionCollection } from "../../../../collections/Permissions";
import { Utils } from "../../../../common/utils";
import { DashboardRange } from "../../../../models/DashboardRange";
import { DashboardWidget } from "../../../../models/DashboardWidget";
import { User } from "../../../../models/User";
import { BaseView } from "../../../_BaseViews";
import { ModifyDashboardWidgetModal } from "./ModifyDashboardWidgetModal";

export interface NewDashboardChannel {
    dashboardId: number;
    widgetId: number;
    channelId: number;
    order: number;
    secondary_order: number;
    name: string;
    critLow: number;
    warnLow: number;
    warnHigh: number;
    critHigh: number;
    rangesList?: DashboardRange[];
}

export interface BaseModifyDashboardWidgetViewOptions extends Backbone.ViewOptions<Backbone.Model> {
    appCollection: AppCollection;
    installCollection: InstallCollection;
    permissionCollection: PermissionCollection;
    organizationCollection: OrganizationCollection;
    dashboardCollection: DashboardCollection;
    dashboardChannelCollection: DashboardChannelCollection;
    dashboardCategoryCollection: DashboardCategoryCollection;
    dashboardTabCollection?: DashboardTabCollection;
    dashboardWidgetCollection: DashboardWidgetCollection;
    moduleCollections: { [key: number]: ModuleCollection };
    dashboardId: number;
    categoryId?: number;
    tabId?: number;
    widgetTypeId: number;
    widgetType: string;
    appTypeId: number;
    appType: string;
    widget: DashboardWidget;
    parentView: ModifyDashboardWidgetModal;
    currentUser: User;
    report?: Backbone.Model;
}


export class BaseModifyDashboardWidgetView extends BaseView {

    options: BaseModifyDashboardWidgetViewOptions;
    thresholdTemplate = _.template(require('templates/modals/dashboard/modify_widgets/shared/threshold.html'));
    advancedConfig = false;
    useCustomLabels = true;

    constructor(options: BaseModifyDashboardWidgetViewOptions) {
        super(options)
    }

    ensureCollectionLoaded() {
        return <any>$.Deferred().resolve()
    }

    show() { }

    preSave(): boolean {
        return true
    }

    getChannelDifference(): (NewDashboardChannel[] | { [key: number]: NewDashboardChannel } | number[])[] {
        return [[], {}, []]
    }

    postSave() {
        return false
    }

    public getCategoryName(): string {
        var val = <string>this.$('.category select').val();
        var text = <string>this.$('.category select option:selected').text();

        // If this is a new category
        //      - val will be the name of the category
        //      - text will be the name of the category followed by " (New Category)"
        // If this is not a new category
        //      - val will be the ID of the category
        //      - text will be the name of the category
        if (text.indexOf(val) === 0) {
            // This is a new category
            return val;
        }

        return text;
    }

    public getCategoryId(): number {
        var val = <string>this.$('.category select').val();
        var text = <string>this.$('.category select option:selected').text();

        // If this is a new category
        //      - val will be the name of the category
        //      - text will be the name of the category followed by " (New Category)"
        // If this is not a new category
        //      - val will be the ID of the category
        //      - text will be the name of the category
        if (text.indexOf(val) === 0) {
            // This is a new category that does not have an id yet.
            return null;
        }

        return Number(val);
    }

    public getTabId(): number | null {
        var val = <string>this.$('.tab select').val();
        if (!val || val === '') return null;
        return Number(val);
    }

    protected reloadCategoriesForTab(tabId: number | null) {
        var self = this;
        var categorySelect = this.$('.category select');
        
        // Check if collection is filtered by tabId - if so, we need to fetch all categories
        var collectionTabId = this.options.dashboardCategoryCollection.tabId;
        
        // If collection is filtered by a different tab, fetch all categories
        if (collectionTabId !== undefined && collectionTabId !== null && collectionTabId !== tabId) {
            var DashboardCategoryCollection = require('../../../../collections/DashboardCategories').DashboardCategoryCollection;
            var allCategoriesCollection = new DashboardCategoryCollection([], {
                dashboardId: this.options.dashboardId
                // No tabId - fetch all categories
            });
            
            allCategoriesCollection.getCollectionFetchPromise().done(() => {
                // Temporarily replace the collection for filtering
                var originalCollection = this.options.dashboardCategoryCollection;
                this.options.dashboardCategoryCollection = allCategoriesCollection;
                this._reloadCategoriesForTabInternal(tabId, categorySelect);
                // Restore original collection
                this.options.dashboardCategoryCollection = originalCollection;
            });
            return;
        }
        
        this._reloadCategoriesForTabInternal(tabId, categorySelect);
    }
    
    private _reloadCategoriesForTabInternal(tabId: number | null, categorySelect: JQuery) {
        
        // Destroy select2 if it exists
        if ((<any>categorySelect).data('select2')) {
            try {
                categorySelect.select2('destroy');
            } catch (e) {
                // Ignore errors if select2 is not properly initialized
            }
        }
        
        // Clear existing options
        categorySelect.empty();
        
        // Filter categories by tab
        // Convert tabId to number for comparison to handle string/number mismatches
        var tabIdNum = tabId !== null ? Number(tabId) : null;
        var categories = this.options.dashboardCategoryCollection.filter((category) => {
            if (category.get('minimal') === 1) return false; // Skip Graph category
            var categoryTabId = category.get('dashboard_tab_id');
            // Convert to number for comparison
            var categoryTabIdNum = categoryTabId !== null && categoryTabId !== undefined ? Number(categoryTabId) : null;
            var matches = false;
            if (tabIdNum === null) {
                matches = categoryTabIdNum === null;
            } else {
                matches = categoryTabIdNum === tabIdNum;
            }
            return matches;
        });
        
        // Build data array for select2
        var categoryData = categories.map(function(category) {
            return {
                id: category.get('id'),
                text: category.get('name')
            };
        });

        // When tab has no categories, add "All Widgets" as default (same as empty dashboard)
        var firstCategoryId: string | number | null = null;
        if (categories.length === 0) {
            categoryData.push({ id: 'All Widgets', text: 'All Widgets' });
            firstCategoryId = 'All Widgets';
        } else {
            firstCategoryId = categories[0].get('id');
        }
        
        // Add categories to select (for fallback/non-select2)
        _.each(categoryData, function(cat) {
            var option = $('<option>', {
                value: String(cat.id),
                text: cat.text
            });
            categorySelect.append(option);
        });
        
        // Select first category by default
        if (firstCategoryId !== null) {
            categorySelect.val(String(firstCategoryId));
        }
        
        // Re-initialize select2 with proper configuration and data
        var select2Options = _.extend({
            tags: true,
            data: categoryData,
            placeholder: categoryData.length === 0 ? 'Type to create a new category' : 'Select or create a category',
            allowClear: false,
            createTag: (params) => {
                if (!params.term || !params.term.length) {
                    return null; // Don't create tag for empty input
                }
                // Check if the term already exists
                var term = params.term.trim();
                var exists = categoryData.some(function(cat) {
                    return cat.text.toLowerCase() === term.toLowerCase();
                });
                if (exists) {
                    return null; // Don't create duplicate
                }
                return {
                    id: term,
                    text: term + ' (New Category)'
                };
            }
        }, Utils.Select2.defaultOptions);
        
        (<any>categorySelect).select2(select2Options);
        
        // Set the value after initialization
        if (firstCategoryId) {
            categorySelect.val(firstCategoryId).trigger('change');
        } else {
            // Clear the value if no categories
            categorySelect.val(null).trigger('change');
        }
    }

    public getWidgetOrder() {
        if (this.options.widget && !this.options.widget.isNew()) {
            // This is an existing widget
            return this.options.widget.get('order');
        }

        // This is a new widget
        var order = 1;
        var categoryId = this.getCategoryId();
        if (categoryId) {
            // This is a new widget in an existing category
            var widgets = this.options.dashboardWidgetCollection.where({
                dashboard_category_id: categoryId
            });
            _.each(widgets, (w) => {
                var wOrder = w.get('order');
                if (wOrder >= order) order = wOrder + 1;
            });
        }
        return order;
    }

    protected initializeCategorySelect(selector: string) {
        (<any>this.$(selector)).select2(_.extend({
            tags: true,
            createTag: (params) => {
                if (!params.term.length) {
                    return {
                        id: 'New Category',
                        text: 'Start typing to create a new Category'
                    }
                } else {
                    return {
                        id: params.term,
                        text: params.term + ' (New Category)'
                    }
                }
            }
        }, Utils.Select2.defaultOptions))
    }

    handleConfigToggleChange(e: JQuery.UIEventBase) {
        this.advancedConfig = $(e.currentTarget).is(':checked');
        // Hide/show regular thresholds
        this.$('.threshold-badges').toggleClass('hidden', this.advancedConfig);
        this.$('.thresholds-limits').toggleClass('hidden', this.advancedConfig);
        this.$('.threshold-multirange-options').toggleClass('hidden', !this.advancedConfig);
        this.$('.thresholds-multirange').toggleClass('hidden', !this.advancedConfig);
        this.$('.thresholds .add-channel').toggleClass('hidden', !this.advancedConfig);

        // Special case for IAQ widget
        this.$('.iaq-custom-labels-config').toggleClass('hidden', !this.advancedConfig);
    }

    handleCustomLabelsToggleChange(e: JQuery.UIEventBase) {
        this.useCustomLabels = $(e.currentTarget).is(':checked');
        // Hide/show regular label input
        this.$('.widget-channel-label-wrapper').toggleClass('hidden', !this.useCustomLabels);
    }

    handleUseDefaultsBtn(e: JQuery.UIEventBase) {
        e.preventDefault();
        const channelSelect = $(e.currentTarget).parents('.channel-selects').find('.widget-channel-select-channel');
        const channelID = Number(channelSelect.val());
       
        const installSelect = $(e.currentTarget).parents('.channel-selects').find('.widget-channel-select-install');
        const installId = Number(installSelect.val());
        const moduleSelect = $(e.currentTarget).parents('.channel-selects').find('.widget-channel-select-module');
        const moduleNumber = moduleSelect.val();
        
        if (!installId || !moduleNumber) return;
        const module = this.options.moduleCollections[installId].findWhere({ module_number: moduleNumber });
        // This contains the channel data with the sensor data aswell
        const channelWithSensorData = module.getChannelById(channelID);
        
        if(!channelWithSensorData){
            return;
        }

        // Get channel type based on sensor type/version and channel position
        const channelType = this.getChannelType(channelWithSensorData);
        const channelUnit = channelWithSensorData.channel_unit;
        const rangesList = this.getDefaultRangesByChannelType(channelType, channelUnit);
        const thresholdsWrapper = $(e.currentTarget).parents('.channel-thresholds').find('.thresholds-wrapper');
        if (rangesList.length > 1) {
            // Remove elements generated by Spectrum Color Picker
            const colorPickerInputs = thresholdsWrapper.find('.threshold-range .threshold-color.color-picker');
            _.each(colorPickerInputs, (cp) => {
                var $cp = $(cp);
                (<any>$cp).spectrum("container").remove();
            });

        thresholdsWrapper.empty();
            this.renderRanges(thresholdsWrapper, rangesList, channelUnit);
            this.handleThresholdInput();
        } else {
            (<any>$).notify({
                title: 'This channel does not have default ranges',
                message: '',
                icon: 'fa fa-exclamation-circle',
            }, {
                animate: {
                    enter: 'animated fadeInUp',
                },
                // The type property defines the css style
                // See alerts.less for available styles
                type: "warning-alt",
                placement: { from: "bottom", align: "center" }
            });
        }
    }

    // Returns the channel type as a string, based on the 
    // sensor type, sensor version and channel position.
    // If no match, an empty string is returned.
    // Possible channel types:
    // "temp" | "hum" | "co2" | "voc" | "pm25" | "pm10" | "pm1" |
    // "hcho" | "no2" | "h2s" | "so2" | "o3" | "co"
    getChannelType(channelWithSensorData){
        const sensorType = channelWithSensorData.sensor_type;
        const sensorVersion = channelWithSensorData.sensor_version;
        const channelPosition = channelWithSensorData.channel_position;
        const channelName = channelWithSensorData.channel_name;
        if(!sensorType || !sensorVersion || !channelPosition) return '';

        if (sensorType === 1) {
            // Internal Sensor
            if (sensorVersion >= 5) {
                switch (channelPosition) {
                    case 1: return 'temp';
                    case 2: return 'hum';
                    default: return '';
                }
            } else {
                switch (channelPosition) {
                    case 2: return 'temp';
                    case 3: return 'hum';
                    default: return '';
                }
            }
        } else if (sensorType === 30) {
            // Old Environmental
            switch (channelPosition) {
                case 1: return 'temp';
                case 2: return 'hum';
                case 3: return 'co2';
                case 4: return 'voc';
                case 10: return 'pm25';
                case 11: return 'pm10';
                default: return '';
            }
        } else if (sensorType === 34) {
            // IAQ
            // Special case for Ventilation Score
            // Check if the channel name contains 'vpi' and it's a virtual channel
            let VPIregex = /vpi/i; // Using the 'i' flag for case-insensitive matching
            if(VPIregex.test(channelName) && channelWithSensorData.channel_virtual){
                return 'vpi';
            }

            // Special case for AQI channels
            // Check if the channel name contains 'vpi' and it's a virtual channel
            let AQIregex = /aqi/i; // Using the 'i' flag for case-insensitive matching
            if(AQIregex.test(channelName) && channelWithSensorData.channel_virtual){
                return 'aqi';
            }

            switch (channelPosition) {
                case 1: return 'temp';
                case 2: return 'hum';
                case 3: return 'co2';
                case 4: return 'voc';
                case 89: return 'pm1';
                case 7: case 90:  return 'pm25';
                case 8: case 92: case 91: return 'pm10';
                case 99: return 'hcho';
                case 100: return 'no2';
                case 103: return 'h2s';
                case 106: return 'so2';
                case 109: return 'o3';
                case 112: return 'co';
                default: return '';
            }
        }
    }

    getDefaultRangesByChannelType(type: string, unit: string) {
        switch (type) {
            case 'temp': //Temerature
                const isCelcius = unit.toLowerCase() === '°c' || unit.toLowerCase() === 'c';
                return [new DashboardRange({
                    name: 'Cold',
                    description: 'Value below threshold',
                    color: Utils.colors.blue,
                    min: null,
                    max: isCelcius ? 18 : 66,
                    icon: 'fa-circle-arrow-down'
                }), new DashboardRange({
                    name: 'Cool',
                    description: 'Value below threshold',
                    color: Utils.colors["light-blue"],
                    min: isCelcius ? 18 : 66,
                    max: isCelcius ? 20 : 68,
                    icon: 'fa-circle-arrow-down'
                }), new DashboardRange({
                    name: 'Comfortable',
                    description: 'Value within threshold',
                    color: Utils.colors.green,
                    is_default: true,
                    min: isCelcius ? 20 : 68,
                    max: isCelcius ? 23 : 74,
                }), new DashboardRange({
                    name: 'Warm',
                    description: 'Value above threshold',
                    color: Utils.colors.yellow,
                    min: isCelcius ? 23 : 74,
                    max: isCelcius ? 25 : 77,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Hot',
                    description: 'Value above threshold',
                    color: Utils.colors.red,
                    min: isCelcius ? 25 : 77,
                    max: null,
                    icon: 'fa-circle-arrow-up'
                })]
            case 'hum': //Humidity
                return [new DashboardRange({
                    name: 'Too Dry',
                    description: 'Value below threshold',
                    color: Utils.colors.red,
                    min: null,
                    max: 20,
                    icon: 'fa-circle-arrow-down'
                }), new DashboardRange({
                    name: 'Dry',
                    description: 'Value below threshold',
                    color: Utils.colors.yellow,
                    min: 20,
                    max: 30,
                    icon: 'fa-circle-arrow-down'
                }), new DashboardRange({
                    name: 'Comfortable',
                    description: 'Value within threshold',
                    color: Utils.colors.green,
                    is_default: true,
                    min: 30,
                    max: 70,
                }), new DashboardRange({
                    name: 'Humid',
                    description: 'Value above threshold',
                    color: Utils.colors.yellow,
                    min: 70,
                    max: 80,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Too Humid',
                    description: 'Value above threshold',
                    color: Utils.colors.red,
                    min: 80,
                    max: null,
                    icon: 'fa-circle-arrow-up'
                })]
            case 'co2': //C02
                return [new DashboardRange({
                    name: 'Good',
                    description: 'Value within threshold',
                    color: Utils.colors.green,
                    min: null,
                    max: 1001,
                    is_default: true,
                }), new DashboardRange({
                    name: 'Moderate',
                    description: 'Value above threshold',
                    color: Utils.colors.yellow,
                    min: 1001,
                    max: 2001,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Unhealthy',
                    description: 'Value above threshold',
                    color: Utils.colors.red,
                    min: 2001,
                    max: 5001,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Very unhealthy',
                    description: 'Value above threshold',
                    color: Utils.colors.purple,
                    min: 5001,
                    max: 40000,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Hazardous',
                    description: 'Value above threshold',
                    color: Utils.colors.maroon,
                    min: 40000,
                    max: null,
                    icon: 'fa-circle-arrow-up'
                })]
            case 'voc': // VOC
                return [new DashboardRange({
                    name: 'Excellent',
                    description: 'Value within threshold',
                    color: Utils.colors.green,
                    min: null,
                    max: 221,
                    is_default: true
                }), new DashboardRange({
                    name: 'Moderate',
                    description: 'Value above threshold',
                    color: Utils.colors.yellow,
                    min: 221,
                    max: 661,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Poor',
                    description: 'Value above threshold',
                    color: Utils.colors.orange,
                    min: 661,
                    max: 2201,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Unhealthy',
                    description: 'Value above threshold',
                    color: Utils.colors.red,
                    min: 2201,
                    max: null,
                    icon: 'fa-circle-arrow-up'
                })]
            case 'pm25': //PM 2.5
                return [new DashboardRange({
                    name: 'Good',
                    description: 'Value within threshold',
                    color: Utils.colors.green,
                    min: null,
                    max: 12.1,
                    is_default: true
                }), new DashboardRange({
                    name: 'Moderate',
                    description: 'Value above threshold',
                    color: Utils.colors.yellow,
                    min: 12.1,
                    max: 35.5,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'USG',
                    description: 'Value above threshold',
                    color: Utils.colors.orange,
                    min: 35.5,
                    max: 55.5,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Unhealthy',
                    description: 'Value above threshold',
                    color: Utils.colors.red,
                    min: 55.5,
                    max: 150.5,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Very Unhealthy',
                    description: 'Value above threshold',
                    color: Utils.colors.purple,
                    min: 150.5,
                    max: 250.5,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Hazardous',
                    description: 'Value above threshold',
                    color: Utils.colors.maroon,
                    min: 250.5,
                    max: null,
                    icon: 'fa-circle-arrow-up'
                })]
            case 'pm10': // PM 10
                return [new DashboardRange({
                    name: 'Good',
                    description: 'Value within threshold',
                    color: Utils.colors.green,
                    min: null,
                    max: 54,
                    is_default: true
                }), new DashboardRange({
                    name: 'Moderate',
                    description: 'Value above threshold',
                    color: Utils.colors.yellow,
                    min: 54,
                    max: 155,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'USG',
                    description: 'Value above threshold',
                    color: Utils.colors.orange,
                    min: 155,
                    max: 255,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Unhealthy',
                    description: 'Value above threshold',
                    color: Utils.colors.red,
                    min: 255,
                    max: 355,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Very Unhealthy',
                    description: 'Value above threshold',
                    color: Utils.colors.purple,
                    min: 355,
                    max: 425,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Hazardous',
                    description: 'Value above threshold',
                    color: Utils.colors.maroon,
                    min: 425,
                    max: null,
                    icon: 'fa-circle-arrow-up'
                })]
                case 'vpi': // Ventilation Score 
                return [ new DashboardRange({
                    name: 'Poor',
                    description: 'Value below threshold',
                    color: Utils.colors.red,
                    min: null,
                    max: 40,
                    icon: 'fa-circle-arrow-up'
                }), new DashboardRange({
                    name: 'Moderate',
                    description: 'Value below threshold',
                    color: Utils.colors.yellow,
                    min: 40,
                    max: 70,
                    icon: 'fa-circle-arrow-down'
                }), new DashboardRange({
                    name: 'Comfortable',
                    description: 'Value within threshold',
                    color: Utils.colors.green,
                    is_default: true,
                    min: 70,
                    max: null,
                })]
                case 'aqi': // Ventilation Score 
                return [new DashboardRange({
                    name: 'Good',
                    description: 'Value within threshold',
                    color: Utils.colors.green,
                    min: null,
                    max: 50,
                    is_default: true,
                }),
                new DashboardRange({
                    name: 'Moderate',
                    description: 'Value above threshold',
                    color: Utils.colors.yellow,
                    min: 50,
                    max: 100,
                    icon: 'fa-circle-arrow-up',
                }),
                new DashboardRange({
                    name: 'USG',
                    description: 'Value above threshold',
                    color: Utils.colors.orange,
                    min: 100,
                    max: 150,
                    icon: 'fa-circle-arrow-up',
                }),
                new DashboardRange({
                    name: 'Unhealthy',
                    description: 'Value above threshold',
                    color: Utils.colors.red,
                    min: 150,
                    max: 200,
                    icon: 'fa-circle-arrow-up',
                }),
                new DashboardRange({
                    name: 'Very Unhealthy',
                    description: 'Value above threshold',
                    color: Utils.colors.purple,
                    min: 200,
                    max: 300,
                    icon: 'fa-circle-arrow-up',
                }),
                new DashboardRange({
                    name: 'Hazardous',
                    description: 'Value above threshold',
                    color: Utils.colors.maroon,
                    min: 300,
                    max: null,
                    icon: 'fa-circle-arrow-up',
                })]
            default:
                return [new DashboardRange({
                    name: 'Good',
                    description: 'Value within threshold',
                    color: Utils.colors.green,
                    min: null,
                    max: null,
                    is_default: true
                })]
        }
    }

    renderRanges(row, rangesList, channelUnit) {
        const $row = $(row);
        let useMin = false;
        _.sortBy(rangesList, r => r.max).forEach(range => {
            if (!(range instanceof DashboardRange)) range = new DashboardRange(range);
            let value;
            if (range.get('is_default')) {
                useMin = true;
                return
            }
            useMin ? value = range.get('min') : value = range.get('max');

            $row.append(this.thresholdTemplate({
                id: range.get('id'),
                name: range.get('name'),
                value: value,
                unit: channelUnit,
                color: range.get('color'),
                aboveThreshold: useMin ? true : false
            }));
            (<any>this.$('input[type="checkbox"]')).bootstrapToggle();
        });

        // Initialize Spectrum color picker
        (<any>$row.find('.color-picker')).spectrum(this.getSpectrumDefaultOptions());

        const channelThresholdsRow = $row.closest('.channel-thresholds');
        this.setMultirangeThresholdQty(channelThresholdsRow);
    }

    handleAddThreshold(e: JQuery.UIEventBase) {
        e.preventDefault()
        var currentWrapper = $(e.currentTarget).closest('.channel-thresholds').find('.thresholds-wrapper');
        var unit = $(e.currentTarget).closest('.thresholds').find('.unit').val();
        if (!unit) unit = $(e.currentTarget).parents('.channel-thresholds').attr('data-channel-unit');

        currentWrapper.append(this.thresholdTemplate({
            id: '',
            name: '',
            value: '',
            unit: unit,
            color: "#e66465",
            aboveThreshold: false
        }));

        (<any>this.$('input[type="checkbox"]')).bootstrapToggle();

        // Initialize Spectrum color picker
        (<any>this.$('.color-picker')).spectrum(this.getSpectrumDefaultOptions());

        var channelThresholdsRow = $(e.currentTarget).closest('.channel-thresholds');
        this.setMultirangeThresholdQty(channelThresholdsRow);
        this.handleThresholdInput();
    }

    handleDeleteThreshold(e: JQuery.UIEventBase) {
        e.preventDefault();
        var channelThresholdsRow = $(e.currentTarget).closest('.channel-thresholds');
        // Remove elements generated by Spectrum Color Picker
        var colorPickerInput = (<any>$(e.currentTarget).closest('.threshold-range').find('.threshold-color.color-picker'));
        colorPickerInput.spectrum("container").remove();
        $(e.currentTarget).parents('.threshold-range').remove();

        this.setMultirangeThresholdQty(channelThresholdsRow);
        this.handleThresholdInput();
    }

    setMultirangeThresholdQty(channelThresholdsRow) {
        var $currentchannelRow = $(channelThresholdsRow);
        var rangesQty = $currentchannelRow.find('.thresholds-wrapper .threshold-range').length;

        if (rangesQty == 0) {
            $currentchannelRow.find('.threshold-multirange-qty').addClass('empty').html('No Thresholds');
        } else {
            $currentchannelRow.find('.threshold-multirange-qty').removeClass('empty').html(`${rangesQty} Threshold${rangesQty > 1 ? 's' : ''}`);
        }
    }

    getSpectrumDefaultOptions() {
        return {
            type: "color",
            showInput: true,
            hideAfterPaletteSelect: true,
            showAlpha: false,
            allowEmpty: false,
            showPaletteOnly: true,
            togglePaletteOnly: true,
            togglePaletteMoreText: 'more',
            togglePaletteLessText: 'less',
            palette: [
                [Utils.colors.blue, Utils.colors["light-blue"], Utils.colors.green, Utils.colors.yellow],
                [Utils.colors.orange, Utils.colors.red, Utils.colors.purple, Utils.colors.maroon],
            ]
        }
    }

    handleThresholdInput() {
        $('.modal-footer .save').prop('disabled', !this.canSaveRanges());
    }

    canSaveRanges() {
        var canSave = true;
        _.each(this.$('.thresholds-wrapper'), (wrapper) => {
            var values = [];

            _.each($(wrapper).find('.threshold-range'), (rangeRow) => {
                var value = $(rangeRow).find('.threshold-value').val();
                var name = $(rangeRow).find('.threshold-name').val();

                // Check for empty name or empty value
                if (name == '' || value == '') {
                    canSave = false;
                    return false;
                }
                values.push(Number(value));
            });

            // Check for repeated values
            if (_.uniq(values).length !== values.length) canSave = false;

            if (!canSave) return false;
        });
        return canSave;
    }

    //Get channel ranges
    getRangesListFromAdvancedThresholds(channelRow) {
        var $row = $(channelRow);

        //Create default range
        var defaultRange = new DashboardRange({
            name: "Good",
            description: 'Value within threshold',
            color: Utils.colors.green,
            is_default: true,
            min: null,
            max: null
        });

        // If there are no ranges, return the default range
        var wrapper: any = $row.find('.thresholds-wrapper');
        if (wrapper.is(':empty')) return [defaultRange];

        // For sensitivity widget -- Get ranges from just the first sensitiviy level
        if (wrapper.length > 1) wrapper = $(wrapper[0]);

        var rangesBelowThreshold = [];
        var rangesAboveThreshold = [];
        _.each(wrapper.find('.threshold-range'), (rangeRow) => {
            var range = new DashboardRange();
            var value = $(rangeRow).find('.threshold-value').val();
            var name = $(rangeRow).find('.threshold-name').val();

            range.set({
                id: $(rangeRow).data('range-id') ?? null,
                name: name,
                description: $(rangeRow).find('.threshold-ab-checkbox').is(':checked') ? 'Value above threshold' : 'Value below threshold',
                aboveThreshold: $(rangeRow).find('.threshold-ab-checkbox').is(':checked') ? true : false,
                val: Number(value),
                color: $(rangeRow).find('.threshold-color').val(),
                is_default: false
            })
            range.get('aboveThreshold') ? range.setIcon('fa-circle-arrow-up') : range.setIcon('fa-circle-arrow-down');
            range.get('aboveThreshold') ? rangesAboveThreshold.push(range) : rangesBelowThreshold.push(range);
        });

        // Sort ranges below threshold by value and assign min and max values
        rangesBelowThreshold = _.sortBy(rangesBelowThreshold, r => r.get('val'));
        _.each(rangesBelowThreshold, (range, idx) => {
            idx == 0 ? range.setMin(null) : range.setMin(rangesBelowThreshold[idx - 1].get('val'));
            range.setMax(range.get('val'))
        });

        // Set min and max values for default range
        defaultRange.set({
            min: _.last(rangesBelowThreshold) ? _.last(rangesBelowThreshold).get('val') : null,
            max: _.head(rangesAboveThreshold) ? _.head(rangesAboveThreshold).get('val') : null
        });

        // Sort ranges above threshold by value and assign min and max values
        rangesAboveThreshold = _.sortBy(rangesAboveThreshold, r => r.get('val'));
        _.each(rangesAboveThreshold, (range, idx) => {
            range.setMin(range.get('val'))
            idx == (rangesAboveThreshold.length - 1) ? range.setMax(null) : range.setMax(rangesAboveThreshold[idx + 1].get('val'));
        });

        // Merge all ranges
        rangesBelowThreshold.push(defaultRange);
        return _.union(rangesBelowThreshold, rangesAboveThreshold);
    }

    getRangesListFromRegularThresholds(channelRow) {
        var $row = $(channelRow);
        var rangesList = [];

        var limitCritLow: any = $row.find('.thresholds .crit-low').val();
        limitCritLow = limitCritLow === '' ? null : Number(limitCritLow);
        if (isNaN(limitCritLow)) limitCritLow = null;

        var limitWarnLow: any = $row.find('.thresholds .warn-low').val();
        limitWarnLow = limitWarnLow === '' ? null : Number(limitWarnLow);
        if (isNaN(limitWarnLow)) limitCritLow = null;

        var limitWarnHigh: any = $row.find('.thresholds .warn-high').val();
        limitWarnHigh = limitWarnHigh === '' ? null : Number(limitWarnHigh);
        if (isNaN(limitWarnHigh)) limitCritLow = null;

        var limitCritHigh: any = $row.find('.thresholds .crit-high').val();
        limitCritHigh = limitCritHigh === '' ? null : Number(limitCritHigh);
        if (isNaN(limitCritHigh)) limitCritLow = null;


        // Critical - Low
        if (limitCritLow != null) {
            const limitCritLowId = $row.find('.thresholds .crit-low').attr('range-id');
            var thresholdRange = new DashboardRange();
            thresholdRange.set({
                id: (limitCritLowId) ? Number(limitCritLowId) : null,
                name: "Critical Low",
                min: null,
                max: limitCritLow,
                color: Utils.colors.red,
                description: 'Value below threshold',
            });
            rangesList.push(thresholdRange);
        }
        // Warning - Low
        if (limitWarnLow != null) {
            const limitWarnLowId = $row.find('.thresholds .warn-low').attr('range-id');
            var thresholdRange = new DashboardRange();
            thresholdRange.set({
                id: (limitWarnLowId) ? Number(limitWarnLowId) : null,
                name: "Warning Low",
                min: null,
                max: limitWarnLow,
                color: Utils.colors.yellow,
                description: 'Value below threshold',
            });
            if (limitCritLow != null) {
                thresholdRange.setMin(limitCritLow);
            }
            rangesList.push(thresholdRange);
        }
        //Create default range
        var thresholdRange = new DashboardRange({
            name: "Good",
            description: 'Value within threshold',
            color: Utils.colors.green,
            is_default: true,
            min: null,
            max: null
        });
        if (limitWarnLow != null) {
            thresholdRange.setMin(limitWarnLow);
        } else if (limitCritLow != null) {
            thresholdRange.setMin(limitCritLow);
        }
        if (limitWarnHigh != null) {
            thresholdRange.setMax(limitWarnHigh);
        } else if (limitCritHigh != null) {
            thresholdRange.setMax(limitCritHigh);
        }
        rangesList.push(thresholdRange);

        // Warning - High
        if (limitWarnHigh != null) {
            const limitWarnHighId = $row.find('.thresholds .warn-high').attr('range-id');
            var thresholdRange = new DashboardRange();
            thresholdRange.set({
                id: (limitWarnHighId) ? Number(limitWarnHighId) : null,
                name: "Warning High",
                min: limitWarnHigh,
                max: null,
                color: Utils.colors.yellow,
                description: 'Value above threshold',
            });
            if (limitCritHigh != null) {
                thresholdRange.setMax(limitCritHigh);
            }
            rangesList.push(thresholdRange);
        }
        // Critical - High
        if (limitCritHigh != null) {
            const limitCritHighId = $row.find('.thresholds .crit-high').attr('range-id');
            var thresholdRange = new DashboardRange();
            thresholdRange.set({
                id: (limitCritHighId) ? Number(limitCritHighId) : null,
                name: "Critical High",
                min: limitCritHigh,
                max: null,
                color: Utils.colors.red,
                description: 'Value above threshold',
            });
            rangesList.push(thresholdRange);
        }

        return rangesList;
    }

    updateThresholdRow(container, channelId?, rangesList?, unit?) {
        var limitCritLowField = container.find('.thresholds .crit-low');
        var limitCritLowSpan = container.find('.threshold-badges .crit-low');

        var limitWarnLowField = container.find('.thresholds .warn-low');
        var limitWarnLowSpan = container.find('.threshold-badges .warn-low');

        var limitWarnHighField = container.find('.thresholds .warn-high');
        var limitWarnHighSpan = container.find('.threshold-badges .warn-high');

        var limitCritHighField = container.find('.thresholds .crit-high');
        var limitCritHighSpan = container.find('.threshold-badges .crit-high');

        limitCritLowField.val('');
        limitWarnLowField.val('');
        limitWarnHighField.val('');
        limitCritHighField.val('');
        limitCritLowSpan.text('N/A').removeClass('danger');
        limitWarnLowSpan.text('N/A').removeClass('warning');
        limitWarnHighSpan.text('N/A').removeClass('warning');
        limitCritHighSpan.text('N/A').removeClass('danger');

        var unitField = container.find('.thresholds .unit');
        unitField.val(unit);

        if (!channelId || rangesList.length == 0) return;

        // Render ranges for existing dchannel dependending on the threshold configuration
        if (rangesList.length > 1) {
            var useMin = false;
            _.sortBy(rangesList, r => r.max).forEach(range => {
                if (!(range instanceof DashboardRange)) range = new DashboardRange(range);
                var value;
                if (range.get('is_default')) {
                    useMin = true;
                    return
                }
                useMin ? value = range.get('min') : value = range.get('max');

                container.find('.thresholds-multirange .thresholds-wrapper').append(this.thresholdTemplate({
                    id: range.get('id'),
                    name: range.get('name'),
                    value: value,
                    unit: unit,
                    color: range.get('color'),
                    aboveThreshold: useMin ? true : false
                }));
                (<any>this.$('input[type="checkbox"]')).bootstrapToggle();
            });

            // Initialize Spectrum color picker
            (<any>container.find('.color-picker')).spectrum(this.getSpectrumDefaultOptions());

            // Update thresholds multirange label with number of thresholds
            container.find('.threshold-multirange-qty').html(`${rangesList.length - 1} Thresholds`);
            container.find('.threshold-multirange-qty').removeClass('empty');

            if (!this.advancedConfig) {
                var channelThresholds = this.getThresholdsLimitsFromRangesList(rangesList);
                var update = (limit, id, field, span, klass) => {
                    if (limit == null) {
                        field.val(null);
                        span.text('N/A').removeClass(klass);
                    } else {
                        field.val(limit);
                        span.text(limit + unit).addClass(klass);
                        field.attr('range-id', id);
                    }
                };

                update(channelThresholds.critLowVal, channelThresholds.critLowId, limitCritLowField, limitCritLowSpan, 'danger');
                update(channelThresholds.warningLowVal, channelThresholds.warningLowId, limitWarnLowField, limitWarnLowSpan, 'warning');
                update(channelThresholds.warningHighVal, channelThresholds.warningHighId, limitWarnHighField, limitWarnHighSpan, 'warning');
                update(channelThresholds.critHighVal, channelThresholds.critHighId, limitCritHighField, limitCritHighSpan, 'danger');
            }
        }
    }

    getThresholdsLimitsFromRangesList(rangesList) {
        const critLow = <any>_.find(rangesList, { name: 'Critical Low' });
        const warningLow = <any>_.find(rangesList, { name: 'Warning Low' });
        const warningHigh = <any>_.find(rangesList, { name: 'Warning High' });
        const critHigh = <any>_.find(rangesList, { name: 'Critical High' });

        return {
            critLowVal: critLow ? critLow.max : null,
            critLowId: critLow ? critLow.id : null,
            warningLowVal: warningLow ? warningLow.max : null,
            warningLowId: warningLow ? warningLow.id : null,
            warningHighVal: warningHigh ? warningHigh.min : null,
            warningHighId: warningHigh ? warningHigh.id : null,
            critHighVal: critHigh ? critHigh.min : null,
            critHighId: critHigh ? critHigh.id : null,
        };
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/modals/dashboard/modify_widgets/_BaseModifyWidgetView.ts